import showAll from "@hey-lady/shared/static/images/events/AllEvents.svg";
import showAllUnselected from "@hey-lady/shared/static/images/events/AllEventsUnselected.svg";
import coachEvents from "@hey-lady/shared/static/images/events/CoachEvents.svg";
import coachEventsUnselected from "@hey-lady/shared/static/images/events/CoachEventsUnselected.svg";
import groupStudy from "@hey-lady/shared/static/images/events/GroupStudy.svg";
import groupStudyUnselected from "@hey-lady/shared/static/images/events/GroupStudyUnselected.svg";
import inPerson from "@hey-lady/shared/static/images/events/InPerson.svg";
import inPersonUnselected from "@hey-lady/shared/static/images/events/InPersonUnselected.svg";
import listenOnly from "@hey-lady/shared/static/images/events/ListenOnly.svg";
import listenOnlyUnselected from "@hey-lady/shared/static/images/events/ListenOnlyUnselected.svg";
import nmEvents from "@hey-lady/shared/static/images/events/NMEvents.svg";
import nmEventsUnselected from "@hey-lady/shared/static/images/events/NMEventsUnselected.svg";
import speak15_30 from "@hey-lady/shared/static/images/events/Speak15.svg";
import speak15_30Unselected from "@hey-lady/shared/static/images/events/Speak15Unselected.svg";
import speak45 from "@hey-lady/shared/static/images/events/Speak45.svg";
import speak45Unselected from "@hey-lady/shared/static/images/events/Speak45Unselected.svg";
import { not } from "ramda";
import { ReactNode } from "react";
import {
  ArticleByAuthor,
  ArticleCategory,
  AvailabilityStatus,
  ConversationKind,
  EnglishSkill,
  EventCategory,
  EventEnglishLevelFilter,
  EventHostedByFilter,
  EventKind,
  EventModel,
  EventPermissions,
  EventTimeFilter,
  EventTopicFilter,
  EventTypeFilter,
  Interest,
  OccupationModel,
  SubscriptionTier,
  UserLocation,
} from "../../../shared/types/graphql.ts";
import { isPostReleaseMember } from "./helpers";

export interface Filters {
  articles: {
    category: ArticleCategory;
    author: ArticleByAuthor;
  };
  people: {
    availability: AvailabilityStatus[];
    membership: MemberType[];
    location: UserLocation[];
    interests: Interest[];
    englishSkills: EnglishSkill[];
    englishLevels: PeopleEnglishLevelOptions[];
    occupations: OccupationModel[];
  };
}
export interface EventFilters {
  events: {
    type: EventTypeFilter;
    host: EventHostedByFilter;
    englishLevel: EventEnglishLevelFilter;
    showFullEvents: boolean;
    speakingEvents: boolean;
    eventDate?: string;
    eventTime?: EventTimeFilter[];
    customTimeFrom?: string;
    customTimeUntil?: string;
    topicId?: string;
    interests?: Interest[];
    englishSkills?: EnglishSkill[];
    occupations?: string[];
    duration?: number[];
    topicFilter?: EventTopicFilter;
    groupStudy?: boolean;
  };
}

export interface AppliedFilterType {
  category: string;
  value: string;
  label: string | ReactNode[];
}

export interface SelectOption {
  id: string | undefined;
  name: string | ReactNode[] | undefined;
}

export enum UrlFilterParams {
  Availability = "availability",
  Type = "type",
  Host = "host",
  EnglishLevel = "level",
  EventDate = "date",
  EventTime = "eventTime",
  CustomTimeFrom = "customTimeFrom",
  CustomTimeUntil = "customTimeUntil",
  Full = "full",
  SpeakingEvents = "speakingEvents",
  GroupStudy = "groupStudy",
  TopicId = "topicId",
  TopicFilter = "topicFilter",
  Interests = "interests",
  EnglishSkills = "englishSkills",
  Occupations = "occupations",
  Duration = "duration",
}

export const AVAILABILITY_FILTER = "availability";
export const MEMBERS_FILTER = "membership";
export const LOCATION_FILTER = "location";
export const INTERESTS_FILTER = "interests";
export const ENGLISH_SKILLS_FILTER = "englishSkills";
export const OCCUPATION_FILTER = "occupations";
export const EVENT_TOPIC_ID_FILTER = "topicId";
export const EVENT_TOPIC_FILTER = "topicFilter";
export const EVENT_DATE_FILTER = "eventDate";
export const EVENT_FULL_EVENTS_FILTER = "showFullEvents";
export const EVENT_DURATION_FILTER = "duration";

export const DEFAULT_PEOPLE_FILTERS = {
  people: {
    availability: [AvailabilityStatus.Shared],
    membership: [],
    location: [],
    interests: [],
    englishSkills: [],
    englishLevels: [],
    occupations: [],
  },
};

export const DEFAULT_EVENTS_FILTERS = {
  events: {
    type: EventTypeFilter.AllEventTypes,
    host: EventHostedByFilter.Anyone,
    englishLevel: EventEnglishLevelFilter.All,
    showFullEvents: false,
    speakingEvents: false,
    groupStudy: false,
    eventDate: undefined,
    eventTime: [EventTimeFilter.Morning, EventTimeFilter.Afternoon, EventTimeFilter.Evening],
    customTimeFrom: undefined,
    customTimeUntil: undefined,
    topicId: undefined,
    interests: [],
    englishSkills: [],
    occupations: [],
    duration: [],
    topicFilter: EventTopicFilter.All,
  },
};

export type PeopleFilterCategory = Exclude<keyof Filters["people"], "availability">;

export type EventsFilterCategory = keyof EventFilters["events"];

export enum MemberType {
  Follow = "Follow",
  LittleSisters = "Little Sisters",
  Members = "Members",
  BigSisters = "Big Sisters",
}

export interface EventsPageLinkState {
  pageName?: string;
  searchParams?: string;
}

export enum PeopleEnglishLevelOptions {
  Intermediate = "Intermediate",
  Advanced = "Advanced",
  Proficient = "Proficient",
}

export enum PresetFilters {
  NMEvents = "NM Events",
  CoachEvents = "Coach Events",
  GroupStudy = "Group Study",
  Speak15_30 = "Speak 15_30",
  Speak45 = "Speak 45+",
  ListenOnly = "Listen Only",
  ShowAll = "Show All",
  InPerson = "In Person",
}

export interface PresetFilterType {
  type: PresetFilters;
  name: ReactNode | string;
  icon: string;
  unselectedIcon: string;
  locked?: boolean;
  isAllowed?: boolean;
  showIcon?: boolean;
  filters: EventFilters["events"];
}

export const PresetFiltersList: PresetFilterType[] = [
  {
    type: PresetFilters.NMEvents,
    name: "EVENTS_PAGE_NM_EVENTS",
    icon: nmEvents,
    unselectedIcon: nmEventsUnselected,
    locked: false,
    showIcon: false,
    filters: {
      type: EventTypeFilter.NewMemberMeetup,
      host: EventHostedByFilter.Anyone,
      englishLevel: EventEnglishLevelFilter.All,
      showFullEvents: false,
      speakingEvents: false,
      eventDate: undefined,
      eventTime: [EventTimeFilter.Morning, EventTimeFilter.Afternoon, EventTimeFilter.Evening],
      topicId: undefined,
      interests: [],
      englishSkills: [],
      occupations: [],
      duration: [],
      topicFilter: EventTopicFilter.All,
      groupStudy: false,
    },
  },
  {
    type: PresetFilters.CoachEvents,
    name: "EVENTS_PAGE_COACH_EVENTS",
    icon: coachEvents,
    unselectedIcon: coachEventsUnselected,
    locked: false,
    showIcon: false,
    filters: {
      type: EventTypeFilter.AllEventTypes,
      host: EventHostedByFilter.HlCoach,
      englishLevel: EventEnglishLevelFilter.All,
      showFullEvents: false,
      speakingEvents: false,
      eventDate: undefined,
      eventTime: [EventTimeFilter.Morning, EventTimeFilter.Afternoon, EventTimeFilter.Evening],
      topicId: undefined,
      interests: [],
      englishSkills: [],
      occupations: [],
      duration: [],
      topicFilter: EventTopicFilter.All,
      groupStudy: false,
    },
  },
  {
    type: PresetFilters.Speak15_30,
    name: "EVENTS_PAGE_SPEAK_15_30",
    icon: speak15_30,
    unselectedIcon: speak15_30Unselected,
    locked: false,
    showIcon: false,
    filters: {
      type: EventTypeFilter.AllEventTypes,
      host: EventHostedByFilter.Anyone,
      englishLevel: EventEnglishLevelFilter.All,
      showFullEvents: false,
      speakingEvents: true,
      eventDate: undefined,
      eventTime: [EventTimeFilter.Morning, EventTimeFilter.Afternoon, EventTimeFilter.Evening],
      topicId: undefined,
      interests: [],
      englishSkills: [],
      occupations: [],
      duration: [15, 30],
      topicFilter: EventTopicFilter.All,
      groupStudy: false,
    },
  },
  {
    type: PresetFilters.GroupStudy,
    name: "EVENTS_PAGE_GROUP_STUDY",
    icon: groupStudy,
    unselectedIcon: groupStudyUnselected,
    locked: false,
    showIcon: false,
    filters: {
      type: EventTypeFilter.AllEventTypes,
      host: EventHostedByFilter.Anyone,
      englishLevel: EventEnglishLevelFilter.All,
      showFullEvents: false,
      speakingEvents: false,
      eventDate: undefined,
      eventTime: [EventTimeFilter.Morning, EventTimeFilter.Afternoon, EventTimeFilter.Evening],
      topicId: undefined,
      interests: [],
      englishSkills: [],
      occupations: [],
      duration: [],
      topicFilter: EventTopicFilter.All,
      groupStudy: true,
    },
  },
  {
    type: PresetFilters.Speak45,
    name: "EVENTS_PAGE_SPEAK_45",
    icon: speak45,
    unselectedIcon: speak45Unselected,
    locked: false,
    showIcon: false,
    filters: {
      type: EventTypeFilter.AllEventTypes,
      host: EventHostedByFilter.Anyone,
      englishLevel: EventEnglishLevelFilter.All,
      showFullEvents: false,
      speakingEvents: true,
      eventDate: undefined,
      eventTime: [EventTimeFilter.Morning, EventTimeFilter.Afternoon, EventTimeFilter.Evening],
      topicId: undefined,
      interests: [],
      englishSkills: [],
      occupations: [],
      duration: [45, 60, 90, 120],
      topicFilter: EventTopicFilter.All,
      groupStudy: false,
    },
  },
  {
    type: PresetFilters.ListenOnly,
    name: "EVENTS_PAGE_LISTEN_ONLY",
    icon: listenOnly,
    unselectedIcon: listenOnlyUnselected,
    locked: false,
    showIcon: false,
    filters: {
      type: EventTypeFilter.ListenOnly,
      host: EventHostedByFilter.Anyone,
      englishLevel: EventEnglishLevelFilter.All,
      showFullEvents: false,
      speakingEvents: false,
      eventDate: undefined,
      eventTime: [EventTimeFilter.Morning, EventTimeFilter.Afternoon, EventTimeFilter.Evening],
      topicId: undefined,
      interests: [],
      englishSkills: [],
      occupations: [],
      duration: [],
      topicFilter: EventTopicFilter.All,
      groupStudy: false,
    },
  },
  {
    type: PresetFilters.InPerson,
    name: "EVENTS_PAGE_IN_PERSON",
    icon: inPerson,
    unselectedIcon: inPersonUnselected,
    locked: false,
    showIcon: false,
    filters: {
      type: EventTypeFilter.InPerson,
      host: EventHostedByFilter.Anyone,
      englishLevel: EventEnglishLevelFilter.All,
      showFullEvents: false,
      speakingEvents: false,
      eventDate: undefined,
      eventTime: [EventTimeFilter.Morning, EventTimeFilter.Afternoon, EventTimeFilter.Evening],
      topicId: undefined,
      interests: [],
      englishSkills: [],
      occupations: [],
      duration: [],
      topicFilter: EventTopicFilter.All,
      groupStudy: false,
    },
  },
  {
    type: PresetFilters.ShowAll,
    name: "EVENTS_PAGE_SHOW_ALL",
    icon: showAll,
    unselectedIcon: showAllUnselected,
    locked: false,
    showIcon: false,
    filters: {
      type: EventTypeFilter.AllEventTypes,
      host: EventHostedByFilter.Anyone,
      englishLevel: EventEnglishLevelFilter.All,
      showFullEvents: false,
      speakingEvents: false,
      eventDate: undefined,
      eventTime: [EventTimeFilter.Morning, EventTimeFilter.Afternoon, EventTimeFilter.Evening],
      topicId: undefined,
      interests: [],
      englishSkills: [],
      occupations: [],
      duration: [],
      topicFilter: EventTopicFilter.All,
      groupStudy: false,
    },
  },
];

export const lockedTypesForNewMembers = [PresetFilters.NMEvents];

// Filters that should show tooltips when locked (based on attended events count)
export const tooltipLockedFilters = [
  PresetFilters.CoachEvents,
  PresetFilters.Speak45,
  PresetFilters.GroupStudy,
];

export const EVENTS_TO_UNLOCK = 3;

export const applyEventBasedLocking = (
  filters: PresetFilterType[],
  attendedEventsCount: number,
  subscriptionTier: SubscriptionTier,
  userCreatedAt?: string | Date,
  kind?: EventPermissions | null,
  isGuestPassUser = false,
  bypassAttendanceLock = false,
): PresetFilterType[] => {
  const isPostReleaseUser = isPostReleaseMember(userCreatedAt);
  const isStaff = kind === EventPermissions.HeyLadyAdmin || kind === EventPermissions.HeyLadyTeam;

  const meetsAttendance = attendedEventsCount >= EVENTS_TO_UNLOCK;
  const shouldUnlockAdvancedFilters =
    !isGuestPassUser &&
    (isStaff || bypassAttendanceLock || not(isPostReleaseUser) || meetsAttendance);
  const isAllowedCoach = ![
    SubscriptionTier.Free,
    SubscriptionTier.Social,
    SubscriptionTier.Supporter,
    SubscriptionTier.Pause,
  ].includes(subscriptionTier);

  return filters.map((filter) => {
    if (tooltipLockedFilters.includes(filter.type)) {
      return {
        ...filter,
        locked:
          !shouldUnlockAdvancedFilters ||
          (!isAllowedCoach && filter.type === PresetFilters.CoachEvents),
        isAllowed: filter.type === PresetFilters.CoachEvents ? isAllowedCoach : true,
      };
    }
    return filter;
  });
};

export const getPresetFiltersList = (
  kind: EventPermissions | null | undefined,
  subscriptionTier: SubscriptionTier,
  attendedEventsCount: number = 0,
  userCreatedAt?: string | Date,
  isGuestPassUser = false,
  bypassAttendanceLock = false,
): PresetFilterType[] => {
  let baseFilters = [...PresetFiltersList];

  if (isGuestPassUser) {
    baseFilters = baseFilters.filter((filter) => filter.type !== PresetFilters.ShowAll);
  }

  if (kind !== EventPermissions.ZeroToOne && !isGuestPassUser) {
    baseFilters = baseFilters.filter((filter) => filter.type !== PresetFilters.NMEvents);
  }

  // Apply event-based locking for CoachEvents, Speak45, and GroupStudy for all users
  const filtersWithEventBasedLocking = applyEventBasedLocking(
    baseFilters,
    attendedEventsCount,
    subscriptionTier,
    userCreatedAt,
    kind,
    isGuestPassUser,
    bypassAttendanceLock,
  );
  const unlockedFilters = filtersWithEventBasedLocking.filter((filter) => !filter.locked);
  const lockedFilters = filtersWithEventBasedLocking.filter((filter) => filter.locked);

  return [...unlockedFilters, ...lockedFilters];
};

export enum LockedEventType {
  Coach = "EVENTS_PAGE_COACH",
  StudyGroup = "EVENTS_PAGE_GROUP_STUDY",
  Speak45 = "EVENTS_PAGE_SPEAK_45",
  MemberHosted = "EVENTS_PAGE_MEMBER_HOSTED",
}

export const getLockedEventTypeForPresetFilter = (
  filterType: PresetFilters,
): LockedEventType | undefined => {
  switch (filterType) {
    case PresetFilters.CoachEvents:
      return LockedEventType.Coach;
    case PresetFilters.GroupStudy:
      return LockedEventType.StudyGroup;
    case PresetFilters.Speak45:
      return LockedEventType.Speak45;
    default:
      return undefined;
  }
};

export const getLockedEventType = (event: EventModel) => {
  const isCoachEvent = event.isCoachEvent;
  const isForNewMember = event.eventCategory === EventCategory.NewMember;
  const isInPersonEvent = event.kind === EventKind.InPerson;
  const isStudyGroup = event.conversationKind === ConversationKind.StudyGroup;
  const is45MinutesPlus =
    event.startDate &&
    event.endDate &&
    (new Date(event.endDate).getTime() - new Date(event.startDate).getTime()) / (1000 * 60) >= 45;

  const isLockedCoachEvent = isCoachEvent && !isInPersonEvent;
  const isLockedStudyGroupEvent = isStudyGroup && !isCoachEvent;
  const isLockedLongEvent =
    is45MinutesPlus && !isCoachEvent && !isStudyGroup && !(isForNewMember || isInPersonEvent);

  if (isLockedCoachEvent) {
    return LockedEventType.Coach;
  } else if (isLockedStudyGroupEvent) {
    return LockedEventType.StudyGroup;
  } else if (isLockedLongEvent) {
    return LockedEventType.Speak45;
  }
  return LockedEventType.MemberHosted;
};
